import axios, { AxiosError, AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import { Authorization, CustomErrorResponse, User } from '../../app/store/types/auth.types';
import { getI18n } from 'react-i18next';
import { localizationMapper } from '../localization';

const AUTH_STORAGE_KEYS = {
  USER: 'current_user',
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  EXPIRES_IN: 'expires_in',
};

const getErrorMessage = (data: any): string => {
  if (!data) return 'Unknown error';

  if (Array.isArray(data)) return data.join(' ');
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.message) return data.message;

  return 'Unknown error';
};

const handleErrorResponse = (error: AxiosError): Promise<any> => {
  const response = error.response as CustomErrorResponse | undefined;

  // Handle network errors
  if (!response) {
    return Promise.reject('Network error');
  }

  const { status, data } = response;

  // Handle special case: Redirect on forbidden
  if (status === 403) {
    window.location.href = '/access-denied';
    return Promise.reject('Access Forbidden');
  }

  // Handle specific status codes
  switch (status) {
    case 400: {
      if (!data) return Promise.reject('Bad Request');

      if (data.error === 'invalid_grant') {
        return Promise.reject('Invalid credentials');
      }

      if (Array.isArray(data[''])) {
        return Promise.reject(data[''].join(' '));
      }

      return Promise.reject(getErrorMessage(data));
    }
    case 401:
      return Promise.reject('Invalid credentials');
    case 404:
      return Promise.reject(data || 'Resource not found');
    case 405:
      return Promise.reject(data || 'Method not allowed');
    default:
      return Promise.reject(getErrorMessage(data) || 'An unexpected error occurred');
  }
};

// Configure axios interceptors
axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    try {
      return handleErrorResponse(error);
    } catch (e) {
      console.error('Error in interceptor:', e);
      return Promise.reject('An unexpected error occurred');
    }
  }
);

axios.interceptors.request.use(config => {
  const auth = getAuthFromStorage();
  const language = localizationMapper(getI18n().language);
  if (auth) {
    config.headers.Authorization = `Bearer ${auth.access_token}`;
  }

  if (language) {
    config.headers['X-App-Language'] = language;
  }
  return config;
});

export const setAuthHeader = (token: string | null): void => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const getUserFromStorage = (): User | null => {
  const userJson = sessionStorage.getItem(AUTH_STORAGE_KEYS.USER);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
};

export const getAuthFromStorage = (): Authorization | null => {
  const accessToken = sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return null;

  return {
    access_token: accessToken,
    refresh_token: sessionStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) || '',
    expires_in: sessionStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_IN) || '',
    token_type: 'bearer',
  };
};

export const decodeJwtToken = (auth: Authorization): { user: User; authorization: Authorization } => {
  const { access_token, refresh_token, expires_in, token_type } = auth;
  const decoded: any = jwtDecode<User>(access_token);

  const accessTokenExpiry = new Date();
  accessTokenExpiry.setSeconds(accessTokenExpiry.getSeconds() + Number(expires_in));

  const user: User = {
    id: decoded.sub,
    username: decoded.name,
    fullname: decoded.fullname,
    email: decoded.email,
    isEnabled: true,
    isLockedOut: false,
    roles: Array.isArray(decoded.role) ? decoded.role : [decoded.role],
    clientId: decoded.clientId,
    clientName: decoded.clientName,
    jobTitle: decoded.jobTitle,
    phoneNumber: decoded.phoneNumber,
    createdByPlatform: decoded.createdByPlatform,
    clientCode: decoded.clientCode,
  };

  const authorization: Authorization = {
    refresh_token,
    expires_in: accessTokenExpiry.toISOString(),
    access_token,
    token_type: token_type || '',
  };

  return { user, authorization };
};

const initializeAuth = (): void => {
  const user = getUserFromStorage();
  if (user) {
    const auth = getAuthFromStorage();
    if (auth?.access_token) {
      setAuthHeader(auth.access_token);
    }
  }
};

initializeAuth();
